import { HomeyAPI, type Experiment } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors';
import { useAttach } from '../../../store/useAttach';

export interface State {
  experimentsData: ExperimentsData | null;
  isLoading: boolean;
  error: unknown;
}

type ExperimentsData = { [key: string]: Experiment };

export type ExperimentsSettingsStoreState = State;

export class ExperimentsSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('experiments-settings');
  }

  override createInitialState() {
    return {
      experimentsData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      this.set({ ...this.createInitialState(), isLoading: false });
      return;
    }

    try {
      const experimentsData = await api.experiments.getExperiments();

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        experimentsData,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'experiments-settings');

    return { experimentsSettings: this.store(selectAll, shallow) };
  }

  async enableExperiment(options: HomeyAPI.ManagerExperiments.EnableExperimentOptions) {
    const api = this.getApi();
    const { id } = options;

    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      throw new Error('Experiments are not supported on Homey Cloud');
    }

    const result = await api.experiments.enableExperiment(options);

    this.set((state) => {
      const currentData = state.experimentsData?.[id];
      // Somehow we have no data for this experiment.
      if (currentData == null) {
        return {};
      }

      return {
        experimentsData: {
          ...state.experimentsData,
          [id]: {
            ...currentData,
            enabled: true,
          },
        },
      };
    });

    return result;
  }

  async disableExperiment(options: HomeyAPI.ManagerExperiments.DisableExperimentOptions) {
    const api = this.getApi();
    const { id } = options;

    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      throw new Error('Experiments are not supported on Homey Cloud');
    }

    const result = await api.experiments.disableExperiment(options);

    this.set((state) => {
      const currentData = state.experimentsData?.[id];
      // Somehow we have no data for this experiment.
      if (currentData == null) {
        return {};
      }

      return {
        experimentsData: {
          ...state.experimentsData,
          [id]: {
            ...currentData,
            enabled: false,
          },
        },
      };
    });

    return result;
  }
}

function selectAll(state: ExperimentsSettingsStoreState) {
  return {
    ...selectBase(state),
    experimentsData: state.experimentsData,
  };
}

export const experimentsSettingsStore = new ExperimentsSettingsStore();
